export type Role = 'patient';

// ─── Pet Profiles ─────────────────────────────────────────────────────────────

export type PetSpecies =
  | 'dog'
  | 'cat'
  | 'rabbit'
  | 'chicken'
  | 'horse'
  | 'goat'
  | 'sheep'
  | 'pig'
  | 'reptile'
  | 'otheranimal'
  // legacy values kept for compatibility with existing data
  | 'bird'
  | 'fish'
  | 'other';

export interface PetProfile {
  id: string;
  user_id: string;
  name: string;
  species: PetSpecies;
  breed?: string;
  dob?: string;         // YYYY-MM-DD
  color: string;        // hex from PET_COLORS
  created_at: string;   // ISO
  // Vet & health info — stored on the pet doc so all co-carers can read it
  vet_practice?: string;
  microchip?: string;
  primary_vet?: string;
  allergies?: string;
}

export const PET_COLORS = [
  '#C17A2E', // primary-brown
  '#8C4B2A', // hover-dark
  '#EDA514', // accent-orange
  '#606060', // text-secondary
  '#E54C4C', // error-red
  '#3D7A78', // positive-green
  '#4d98e2', // respiratory-blue
] as const;

export type PetColor = (typeof PET_COLORS)[number];

export const PET_SPECIES_EMOJI: Record<PetSpecies, string> = {
  dog: '🐕',
  cat: '🐈',
  rabbit: '🐇',
  chicken: '🐔',
  horse: '🐎',
  goat: '🐐',
  sheep: '🐑',
  pig: '🐖',
  reptile: '🦎',
  otheranimal: '🐾',
  bird: '🐦',
  fish: '🐟',
  other: '🐾',
};

export interface UserProfile {
  id: string;
  role: Role;
  full_name: string;
  email: string;
  profession?: string;
  transplant_date?: string;
  dob?: string;
  hospital_name?: string;
  process_number?: string;
  allergies?: string;
  surgeon?: string;
  subscription_plan?: string;
  subscription_status?: string;
  subscription_started_at?: string;
  subscription_next_payment_at?: string;
  subscription_expires_at?: string;
  is_admin?: boolean;
}

export interface DailyLog {
  id: string;
  user_id: string;
  timestamp: string; // ISO string
  // Vitals — Step 1: Heart Rate
  heart_rate_count?: number;  // beats counted in 15 s
  heart_rate_bpm?: number;    // heart_rate_count × 4
  // Vitals — Step 2: Respiratory Rate
  resp_rate_count?: number;   // breaths counted in 15 s
  resp_rate_bpm?: number;     // resp_rate_count × 4
  // Vitals — Step 3: Temperature
  temp_c?: number;
  // Wellness Check panel
  weight_kg?: number;
  fluid_in_ml?: number;
  food_in_mg?: number;
  mucous_membrane?: 'pink' | 'red' | 'blue' | 'white';
  // Observations
  observations_text?: string;
  mood?: number; // 0=Terrible, 1=Not great, 2=Okay, 3=Good, 4=Great
}

export interface Medication {
  id: string;
  user_id: string;
  name: string;
  dosage_mg: string;
  color_hex: string;
  shape_type:
    | 'capsule1'
    | 'capsule2'
    | 'capsule3'
    | 'diamond'
    | 'round1'
    | 'round2'
    | 'triangle'
    | 'feedingsyringe'
    | 'pipette';
  timeslots: string[]; // Array of times in HH:mm format (e.g., ['08:00', '20:00'])
  treatment_start?: string; // YYYY-MM-DD (inclusive)
  treatment_end?: string;   // YYYY-MM-DD (inclusive). Omit for ongoing treatment.
  reminders_enabled?: boolean;
}

export interface Contact {
  id: string;
  user_id: string;
  name: string;
  // `Nephrologist` is kept for legacy records; new writes should use `Vet`.
  role: 'Vet' | 'Pharmacy' | 'Emergency' | 'Other' | 'Nephrologist';
  phone_number: string;
  email?: string;
}

export type DocumentCategory =
  | 'labs'
  | 'scans'
  | 'letters'
  | 'discharge'
  | 'prescriptions'
  | 'other';

export interface VaultDocument {
  id: string;
  user_id: string;
  category: DocumentCategory;
  file_name: string;
  file_size: number;
  mime_type: string;
  storage_path: string;
  download_url: string;
  title?: string;
  note?: string;
  doc_date?: string; // YYYY-MM-DD
  created_at: string; // ISO
}

// ─── BEAN Gamification ────────────────────────────────────────────────────────

export type XPSource =
  | 'daily_log'        // +15 XP: afternoon vitals completed
  | 'med_log'          // +10 XP: medication taken/logged
  | 'guide_article'    // +10 XP: guide article opened/read
  | 'streak_milestone' // +50 XP: 7/14/30-day streak bonus
  | 'first_login'      // +20 XP: welcome bonus
  | 'lab_glossary'     // +25 XP: Lab Glossary opened
  | 'medical_card'    // +25 XP: Medical ID Card created
  | 'how_to'          // +25 XP: How To vitals page opened
  | 'onboarding';     // +30 XP: Completed the first-launch walkthrough

export const XP_AMOUNTS: Record<XPSource, number> = {
  daily_log: 15,
  med_log: 10,
  guide_article: 10,
  streak_milestone: 50,
  first_login: 20,
  lab_glossary: 25,
  medical_card: 25,
  how_to: 25,
  onboarding: 30,
};

export const BEAN_LEVEL_THRESHOLDS = [0, 100, 250, 500, 900, 1400, 2000, 2800, 3800, 5000];

/** Per-pet BEAN gamification profile — stored at users/{ownerUid}/pets/{petId}/bean/profile */
export interface BeanProfile {
  xp_total: number;
  level: number;                       // 1–10, derived from BEAN_LEVEL_THRESHOLDS
  current_streak: number;              // consecutive days with at least one log
  longest_streak: number;
  last_log_date: string | null;        // ISO date string YYYY-MM-DD
  prev_streak?: number;                // snapshot before today's checkStreak — used for rollback
  prev_log_date?: string | null;       // snapshot before today's checkStreak — used for rollback
  last_awarded: Partial<Record<XPSource, string>>; // ISO date — dedup once per day
  roomie_consent: boolean;
  roomie_group_id: string | null;
  roomie_nickname: string | null;      // user-chosen, max 12 chars
  roomie_color: string | null;         // hex from BEAN_COLORS palette
  /** Show this companion on the public Koda Companions leaderboard. Defaults to true when missing. */
  public_view?: boolean;
  /** Monday ISO date (YYYY-MM-DD) marking the start of the current weekly leaderboard window. */
  week_start?: string | null;
  /** xp_total snapshot taken at the start of the current weekly window. */
  week_xp_start?: number;
  /** Selected companion story chip key (see STORY_CHIPS). 'none' = no chip. */
  story_chip?: string | null;
  /** ISO timestamp at which the story chip auto-expires. Deprecated — stories no longer expire. */
  story_chip_expires?: string | null;
  /** Free-text companion story (max ~140 chars). Shown beneath the leaderboard row. */
  story_text?: string | null;
}

/** Curated emoji+label set for the public Companion Story chip. */
export const STORY_CHIPS: ReadonlyArray<{ key: string; emoji: string }> = [
  { key: 'recovered',     emoji: '🎉' },
  { key: 'vaccinated',    emoji: '💉' },
  { key: 'grooming',      emoji: '✂️' },
  { key: 'vetVisit',      emoji: '🩺' },
  { key: 'newTreat',      emoji: '🦴' },
  { key: 'birthday',      emoji: '🎂' },
  { key: 'feelingGreat',  emoji: '🌟' },
  { key: 'restingUp',     emoji: '😴' },
  { key: 'newHome',       emoji: '🏡' },
] as const;

/** Story chips auto-expire after this duration. Deprecated — retained for legacy data only. */
export const STORY_CHIP_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** Maximum length for the free-text Companion Story. */
export const STORY_TEXT_MAX_LEN = 140;

/** Public Koda Companions leaderboard entry — stored at public_companions/{userUid_petId} */
export interface CompanionEntry {
  id: string;                          // = `${user_id}_${pet_id}`
  user_id: string;
  pet_id?: string;
  pet_owner_uid?: string;
  pet_name: string;                    // active pet name
  pet_color: string;                   // hex from PET_COLORS / BEAN_COLORS
  pet_species?: PetSpecies;            // species of the active pet (for icon)
  level: number;
  xp_total: number;
  current_streak: number;
  longest_streak: number;
  score: number;                       // level * 100 + current_streak
  /** XP gained inside the current weekly window. Used for the "This Week" leaderboard. */
  week_score?: number;
  /** Monday ISO date marking the weekly window this row's week_score belongs to. */
  week_start?: string;
  /** Public companion story chip key (see STORY_CHIPS). */
  story_chip?: string | null;
  /** ISO timestamp at which the story chip auto-expires; rows past expiry are rendered without a chip. Deprecated — stories no longer expire. */
  story_chip_expires?: string | null;
  /** Free-text companion story shown under the row. */
  story_text?: string | null;
  public_view: boolean;
  updated_at: string;                  // ISO
  reactions?: Record<string, string[]>; // emoji → [user_ids who reacted]
}

export const BEAN_COLORS = [
  '#1976d2', // Blue (default)
  '#e53935', // Red
  '#43a047', // Green
  '#fb8c00', // Orange
  '#8e24aa', // Purple
  '#00acc1', // Teal
  '#f4511e', // Deep Orange
  '#6d4c41', // Brown
] as const;

export type BeanColor = typeof BEAN_COLORS[number];

/** Individual XP award event — stored at users/{uid}/fellowship_xp_events */
export interface XPEvent {
  id: string;
  source: XPSource;
  amount: number;
  timestamp: string; // ISO
}

/** A single entry in the Roomies social feed — stored at roomie_groups/{groupId}/feed */
export interface RoomieFeedEntry {
  id: string;
  user_id: string;             // the acting user
  nickname: string;
  bean_color: string;
  action: 'daily_log' | 'streak_milestone' | 'guide_article' | 'joined_group' | 'changed_look';
  streak?: number;             // populated for streak_milestone and daily_log
  timestamp: string;           // ISO
  reactions: Record<string, string[]>;          // emoji → [user_ids who reacted]
  reaction_nicknames?: Record<string, string[]>; // emoji → [nicknames of reactors]
}

// ─── Co-carer / Pet Members ───────────────────────────────────────────────────

export type PetMemberRole = 'owner' | 'carer';

/** Stored at users/{ownerUid}/pets/{petId}/members/{uid} */
export interface PetMember {
  uid: string;
  display_name: string;
  email: string;
  role: PetMemberRole;
  joined_at: string; // ISO
}

/** Stored at users/{ownerUid}/pets/{petId}/invites/{inviteId} */
export interface PetInvite {
  id: string;
  pet_id: string;
  owner_uid: string;
  invited_by_name: string;
  email?: string;           // optional — share-link invites have no email
  status: 'pending' | 'accepted' | 'declined';
  created_at: string; // ISO
  token: string;      // random token used in the accept deep-link
}

/** Stored at users/{carerUid}/shared_pet_refs/{petId} — a pointer to a shared pet */
export interface PetSharedRef {
  pet_id: string;
  owner_uid: string;
  pet_name: string;
  species: PetSpecies;
  color: string;
  breed?: string;
  dob?: string;        // YYYY-MM-DD
  role: PetMemberRole;
  accepted_at: string; // ISO
}

// ─── Medication Dose Claiming ─────────────────────────────────────────────────

/**
 * Stored at users/{ownerUid}/pets/{petId}/medications/{medId}/doses/{doseId}
 * doseId format: "{YYYY-MM-DD}_{HH:mm}"
 */
export interface DoseEntry {
  id: string;
  med_id: string;
  med_name: string;
  scheduled_at: string;     // ISO
  status: 'pending' | 'given' | 'skipped';
  given_by?: string;         // uid of the carer who marked it given
  given_by_name?: string;
  given_at?: string;         // ISO
}
